import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { VerifiedDeceasedService } from '@shared/services/verified-deceased/verified-deceased.service';
import {
  VerifyDecedentResponse,
  VerifyDecedentService,
} from '@shared/services/verify-decedent/verify-decedent.service';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs';

import { testMonkeyContinueScenario } from '../../../../test-monkey/store';
import { selectTestMonkeyState } from '../../../../test-monkey/store/selectors/test-monkey.selectors';
import { ClearStart, TrackProcessError } from '../../actions';
import {
  DecedentIdExceededAttempts,
  IsDeathVerifiedUpdate,
  OpenDecedentInfoReviewModal,
} from '../../actions/decedent-info/decedent-info.actions';
import * as routerActions from '../../actions/router/router.actions';
import { closeSpinner, openSpinner } from '../../actions/spinner/spinner.actions';
import {
  clientSearchError,
  verifyDecedentEnd,
  verifyDecedentStart,
} from '../../actions/verify-decedent/verify-decedent.actions';
import * as fromRootStore from '../../index';
import {
  selectDecedentIdExceededAttempts,
  selectDecedentInfoState,
  selectVerifyDecedentPayload,
} from '../../selectors/decedent-info/decedent-info.selectors';

@Injectable()
export class VerifyDecedentEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private verifyDecedentService: VerifyDecedentService,
    private verifiedDeceasedService: VerifiedDeceasedService,
    private loggerService: LoggerService,
  ) {}

  verifyDecedent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyDecedentStart),
      withLatestFrom(
        this.store$.pipe(select(selectVerifyDecedentPayload)),
        this.store$.pipe(select(selectDecedentIdExceededAttempts)),
      ),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(([, verifyDecedentPayload, decedentIdExceededAttempts]) => {
        return this.verifyDecedentService.verifyDecedent(verifyDecedentPayload).pipe(
          switchMap((serviceResponse: VerifyDecedentResponse) => {
            if (serviceResponse.recentDecedentPoid === '0' && !decedentIdExceededAttempts) {
              return [closeSpinner(), OpenDecedentInfoReviewModal(), DecedentIdExceededAttempts()];
            } else if (serviceResponse.recentDecedentPoid === '0') {
              this.loggerService.info(
                { message: 'Decedent not found, sending to doc upload' },
                LoggerCodes.DECEASED_FALSE,
              );
              return [routerActions.Next()];
            } else {
              //Force (isDeathVerified = false) until you call verifiedDeceased()
              //B/c in the past, isDeathVerified is set by "notification of death" instead of "verification of death"
              return [
                verifyDecedentEnd({
                  payload: {
                    ...serviceResponse,
                    verifiedDeceased: false,
                  },
                }),
              ];
            }
          }),
          catchError(() => {
            return [
              TrackProcessError({ errorCode: 'coo-inheritance-internal_client_search_api_error' }),
              clientSearchError(),
            ];
          }),
        );
      }),
    ),
  );

  verifyDecedentError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clientSearchError),
      withLatestFrom(this.store$.pipe(select(selectDecedentIdExceededAttempts))),
      switchMap(([, decedentIdExceededAttempts]) => {
        if (decedentIdExceededAttempts) {
          return [routerActions.Next()];
        } else {
          return [closeSpinner(), OpenDecedentInfoReviewModal(), DecedentIdExceededAttempts()];
        }
      }),
    ),
  );

  verifyDecedentEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyDecedentEnd),
      withLatestFrom(
        this.store$.pipe(select(selectDecedentInfoState)),
        this.store$.pipe(select(selectTestMonkeyState)),
      ),
      switchMap(([, decedentInfo, monkeyState]) => {
        if (monkeyState?.monkeyMood) {
          return [
            IsDeathVerifiedUpdate({ payload: { isDeathVerified: monkeyState.isDeathVerified } }),
            testMonkeyContinueScenario(),
          ];
        }
        if (monkeyState?.isDeathVerified) {
          return [
            IsDeathVerifiedUpdate({ payload: { isDeathVerified: true } }),
            routerActions.Next(),
          ];
        }
        return this.verifiedDeceasedService
          .verifiedDeceased({ decedentPoid: decedentInfo.decedentPoid })
          .pipe(
            switchMap((verifiedDeceasedResponse) => {
              if (verifiedDeceasedResponse.isDeceased) {
                this.loggerService.info(
                  { message: 'Verified deceased true, skipping document upload page' },
                  LoggerCodes.DECEASED_TRUE,
                );
                return [
                  IsDeathVerifiedUpdate({
                    payload: {
                      isDeathVerified: verifiedDeceasedResponse.isDeceased,
                    },
                  }),
                  routerActions.Next(),
                ];
              } else {
                this.loggerService.info(
                  { message: 'Verified deceased false, calling external client search' },
                  LoggerCodes.DECEASED_FALSE,
                );
                return [ClearStart()];
              }
            }),
            catchError(() => {
              this.loggerService.info(
                { message: 'Verified deceased false, calling external client search' },
                LoggerCodes.DECEASED_FALSE,
              );
              return [ClearStart()];
            }),
          );
      }),
    ),
  );
}
